import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@aura/components/styles'
import Grid from '@aura/components/grid'
import { useTranslation } from 'react-i18next'
import FactorType from '../../../../constants/factor-types'
import ClientServicingFactorTypes from '../../../../constants/client-servicing-factor-types'
import ErrorPage from '../../../../components/error-page'
import FooterNew from '../../../../components/footer-new'
import AccountRecovery from '../accountRecovery'
import AuthenticatorCode from '../authenticatorCode'
import Authenticators from '../authenticators'
import MFAEnrollment from '../mfa-enrollment'
import RecoverySelection from '../recoverySelection'
import DeviceType from '../deviceType'
import Push from '../push'
import QrCode from '../qrCode'
import QrCodeCannotScan from '../qrCodeCannotScan'
import SignInAuth from '../signInAuth'
import { hideLoader, showLoader } from '../../../../utils/loader-actions'
import FetchAccountRecoveryStatus from '../../../../constants/fetch-account-recovery'
import { isSafeRedirectUrl } from '../../../../utils/url-validation'
import styles from './styles'


const IFRAME_ALLOWED_DOMAINS = [ 'rbccm.com', 'rbc.com', 'sterbccm.com', 'fg.rbc.com', 'devfg.rbc.com', 'saifg.rbc.com', 'google-analytics.com' ]


const Component = ({
    appGAURL,
    authData,
    authVerified,
    authenticatorData,
    authenticatorStatus,
    classes,
    dataQRCode,
    error,
    errorSession,
    initAuthenticators,
    initEnrollAuthFactor,
    initPush,
    isProcessing,
    isVerified,
    mfaRequired,
    onActivateAuthenticatorCode,
    onBackToAuthenticators,
    onBackToOptionalAuthenticators,
    onBackFallbackEnforceMFA,
    onBackFallbackEnforceMFATwoSteps,
    onFinish,
    onQRCodeCannotScan,
    onQRCodeCannotScanNext,
    onQRCodeNext,
    onResendAuthChallenge,
    onResendChallenge,
    onSendChallenge,
    onSetAuthenticator,
    onSetDeviceType,
    onSkipEnrollment,
    onVerifyAccountRecovery,
    onVerifyAuthenticatorCode,
    resetAuthVerified,
    qrCodeStatus,
    resendSuccess,
    resendSuccessMsg,
    stepNumber,
    getAccountRecovery,
    accountRecoveryData,
    accountRecoveryStatus,
    onEnroll,
    onEnrollInit,
    validatePhoneAtLogin,
    OnValidatePhoneAtLogin
}) => {
    const { t: translate } = useTranslation()
    // Use authData to determine the flow at this level
    const [ isMfaRequiredFlow ] = useState(
        authData && authData.status === 'MFA_REQUIRED'
    )
    const [ mfaEnroll ] = useState(authData && authData.status === 'MFA_ENROLL')
    const [ factor, setFactor ] = useState()
    const [ signInFactor, setSignInFactor ] = useState()
    const [ canSkipAuthenticators, setCanSkipAuthenticators ] = useState(
        authData.canSkip
    )

    const [ selectedRecoveryType, setSelectedRecoveryType ] = useState(null)

    const [ fallbackEnforceMFA ] = useState(
        authData && authData.status === 'MFA_REQUIRED' && authData.mfaFactors && authData.mfaFactors[0].isAccountRecovery
    )

    const [ validateAR ] = useState(authData && authData.validateRecovery)

    const [ offerMFA ] = useState(authData && authData.offerMFA)

    // Track where user came from to handle back navigation correctly
    const [ cameFromQrCode, setCameFromQrCode ] = useState(false)

    const handleSelectType = useCallback(type => {
        setSelectedRecoveryType(type)
    }, [])

    useEffect(() => {
        if (selectedRecoveryType) {
            if (validatePhoneAtLogin) {
                OnValidatePhoneAtLogin({ recoveryType: selectedRecoveryType })
            } else {
                if (authData && authData?.validatePhoneAtLogin === true) {
                    onSendChallenge({ recoveryType: selectedRecoveryType })
                } else {
                    onEnroll({ recoveryType: selectedRecoveryType })
                }
            }
        }
    }, [ selectedRecoveryType, validatePhoneAtLogin ])

    useEffect(() => {
        if (
            authData
            && authData.mfaFactors
            && authData.mfaFactors.length === 1
        ) {
            setSignInFactor(authData.mfaFactors[0])
            setCanSkipAuthenticators(authData.mfaFactors[0].canSkip)
        }
    }, [])

    const handleAuthResendPush = () => {
        onResendAuthChallenge()
    }

    const handleMFA = factor => {
        setFactor(factor)
        onSetAuthenticator()
    }

    const handleInitPush = values => {
        initPush({ ...values, factorType: FactorType.PUSH })
    }

    const handleVerifyAccountRecovery = values => {
        onVerifyAccountRecovery({
            ...values,
            enrollingInMfa: ((!offerMFA && validateAR) || authData?.validatePhoneAtLogin === true) ? false : authData.enrollingInMfa,
        })
    }

    const handleAuthenticatorCode = values => {
        onActivateAuthenticatorCode({
            ...values,
            factorType: factor.factorType,
        })
    }

    const handleVerifyTotp = values => {
        onVerifyAuthenticatorCode({
            ...values,
            factorId: signInFactor.factorId,
            factorType: FactorType.TOTP,
        })
    }

    const handleSignInAuth = values => {
        setSignInFactor(values)
        mandatoryMfaFlow(0)
    }

    const handleQRCodeNext = () => {
        if (factor.factorType === ClientServicingFactorTypes.PUSH) {
            showLoader()
            setTimeout(() => {
                hideLoader()
                isMfaRequiredFlow
                    ? onBackToAuthenticators()
                    : onBackToOptionalAuthenticators()
            }, 3000)
        } else {
            setCameFromQrCode(true)
            onQRCodeNext()
        }
    }

    // TODO: Tech debt, all steps should be managed by a single reducer that takes
    // an argument of what step to go to
    const handleFallbackQRCodeNext = () => {
        if (factor.factorType === ClientServicingFactorTypes.PUSH) {
            // For PUSH factors in fallbackEnforceMFAFlow, go back to step 2 (Authenticators)
            // Go back 2 steps: step 4 -> 2
            showLoader()
            setTimeout(() => {
                hideLoader()
                onBackFallbackEnforceMFATwoSteps()    
            }, 3000)
            
        } else {
            setCameFromQrCode(true)
            onQRCodeNext()
        }
    }

    // Custom back handler for AuthenticatorCode - goes back to QrCode if came from there, otherwise previous step
    const handleAuthenticatorCodeBack = () => {
        if (cameFromQrCode) {
            setCameFromQrCode(false)
            onBackFallbackEnforceMFATwoSteps() // Go back 2 steps: 7 -> 5
        } else {
            onBackFallbackEnforceMFA() // Go back 1 step: 7 -> 6
        }
    }

    const optionalMfaFlow = step => {
        // Possible Scenarios:
        // 1. (Will be handled by OAW, direct to CP) offerMFA = false && validateAR = false
        // 2. offerMFA = false && validateAR = true (We skip to step 1)
        // 3. offerMFA = true (Normal flow no matter what validateAR is)
        const adjustedStep = (!offerMFA && validateAR) ? step + 1 : step
        console.log('optionalMfaFlow FLOW:', adjustedStep)
        switch (adjustedStep) {
            case 0:
                return (
                    <MFAEnrollment
                        {...{
                            canSkip: authData.canSkip,
                            error,
                            isProcessing,
                            onEnroll: onEnrollInit,
                            onSkip: onSkipEnrollment,
                            userData: authData
                        }}
                    />
                )
            case 1:
                return <RecoverySelection {...{
                    accountRecoveryData,
                    accountRecoveryStatus,
                    getAccountRecovery,
                    onSelectType: handleSelectType,
                    userData: authData,
                }} />
            case 2:
                return (
                    <AccountRecovery
                        {...{
                            data: authData,
                            error,
                            isBackAllowed: false,
                            isProcessing,
                            isVerified,
                            onNext: handleVerifyAccountRecovery,
                            onResendCode: onResendChallenge,
                            onResendSuccess: resendSuccessMsg,
                            OnValidatePhoneAtLogin,
                            selectedRecoveryType,
                            validatePhoneAtLogin,
                        }}
                    />
                )
            case 3:
                return (
                    <Authenticators
                        {...{
                            authenticatorStatus,
                            canSkip: true,
                            data: authenticatorData,
                            error,
                            initAuthenticators,
                            isProcessing,
                            mfaRequired,
                            onFinish,
                            onNext: handleMFA
                        }}
                    />
                )
            case 4:
                return (
                    <DeviceType
                        {...{
                            appName: factor.appName,
                            onBack: onBackFallbackEnforceMFA,
                            onNext: onSetDeviceType,
                            provider: factor.provider,
                            title: factor.title,
                            verbiage: factor.deviceTypeVerb,
                        }}
                    />
                )
            case 5:
                return (
                    <QrCode
                        {...{
                            data: dataQRCode,
                            error,
                            factorType: factor.factorType,
                            initEnrollAuthFactor,
                            isBackAllowed: true,
                            onBack: onBackFallbackEnforceMFA,
                            onNext: handleQRCodeNext,
                            onQRCodeCannotScan,
                            provider: factor.provider,
                            qrCodeStatus,
                            title: factor.title,
                            verbiage: factor.qrCodeVerb,
                        }}
                    />
                )
            case 6:
                return (
                    <QrCodeCannotScan
                        {...{
                            data: dataQRCode,
                            isBackAllowed: true,
                            onBack: onBackFallbackEnforceMFA,
                            onNext: onQRCodeCannotScanNext,
                            provider: factor.provider,
                            title: factor.title,
                            verbiage: factor.qrCodeCannotScanVerb,
                        }}
                    />
                )
            case 7:
                return (
                    <AuthenticatorCode
                        {...{
                            authVerified,
                            error,
                            isBackAllowed: true,
                            isOptionalMfa: true,
                            isProcessing,
                            onBack: handleAuthenticatorCodeBack,
                            onNext: handleAuthenticatorCode,
                            resetAuthVerified,
                            title: factor.title,
                            verbiage: factor.appName,
                        }}
                    />
                )
            default:
                return 'Unknown step' + step
        }
    }

    const mandatoryMfaFlow = step => {
        console.log('mandatoryMfaFlow FLOW:', step)
        switch (step) {
            case 0:
                if (!authData || !authData.mfaFactors) {
                    return <></>
                } else if (!signInFactor && authData.mfaFactors.length > 1) {
                    return (
                        <SignInAuth
                            {...{
                                authenticatorStatus,
                                classes,
                                data: authData.mfaFactors,
                                onSignIn: handleSignInAuth,
                            }}
                        />
                    )
                } else if (signInFactor) {
                    switch (signInFactor.factorType) {
                        case FactorType.CALL:
                        case FactorType.SMS:
                            return (
                                <AccountRecovery
                                    {...{
                                        data: signInFactor,
                                        error,
                                        isBackAllowed: false,
                                        isProcessing,
                                        onNext: onVerifyAccountRecovery,
                                        onResendCode: onResendChallenge,
                                        onResendSuccess: resendSuccessMsg,
                                    }}
                                />
                            )
                        case FactorType.TOTP:
                            return (
                                <AuthenticatorCode
                                    {...{
                                        authVerified,
                                        error,
                                        isBackAllowed: false,
                                        isOptionalMfa: false,
                                        isProcessing,
                                        onNext: handleVerifyTotp,
                                        resetAuthVerified,
                                        title: signInFactor.title || '',
                                        verbiage: signInFactor.appName,
                                    }}
                                />
                            )
                        case FactorType.PUSH:
                            return (
                                <Push
                                    {...{
                                        error,
                                        factorId: signInFactor.factorId,
                                        initPush: handleInitPush,
                                        onResend: handleAuthResendPush,
                                        resendSuccess,
                                        resendSuccessMsg,
                                        title: signInFactor.title || '',
                                        verbiage: signInFactor.descSignIn,
                                    }}
                                />
                            )
                    }
                } else {
                    return <></>
                }
                break
            case 1:
                if (authData && authData.validatePhoneAtLogin === true) {
                    return <RecoverySelection {...{
                        accountRecoveryData: authData.accountRecovery,
                        accountRecoveryStatus:  FetchAccountRecoveryStatus.SUCCESS,
                        onSelectType: handleSelectType,
                        userData: authData,
                    }} />
                } else {
                    return <></>
                }
            case 2:
                return (
                    <AccountRecovery
                        {...{
                            data: authData,
                            error,
                            isBackAllowed: false,
                            isProcessing,
                            isVerified,
                            onNext: handleVerifyAccountRecovery,
                            onResendCode: onResendChallenge,
                            onResendSuccess: resendSuccessMsg,
                            OnValidatePhoneAtLogin,
                            selectedRecoveryType,
                            validatePhoneAtLogin,
                        }}
                    />
                )
            default:
                return <></>
        }
    }

    const fallbackEnforceMFAFlow = step => {
        console.log('fallbackEnforceMFAFlow FLOW:', step)
        switch (step) {
            case 0:
                return (
                    <MFAEnrollment
                        {...{
                            canSkip: !fallbackEnforceMFA,
                            error,
                            isProcessing,
                            onEnroll: onSetAuthenticator,
                        }}
                    />
                )
            case 1:
                return <RecoverySelection {...{
                    accountRecoveryData,
                    accountRecoveryStatus,
                    getAccountRecovery,
                    onSelectType: handleSelectType,
                    userData: authData
                }} />
            case 2:
                return (
                    <AccountRecovery
                        {...{
                            data: authData,
                            error,
                            isBackAllowed: false,
                            isProcessing,
                            isVerified,
                            onNext: handleVerifyAccountRecovery,
                            onResendCode: onResendChallenge,
                            onResendSuccess: resendSuccessMsg,
                            OnValidatePhoneAtLogin,
                            selectedRecoveryType,
                            validatePhoneAtLogin,
                        }}
                    />
                )
            case 3:
                return (
                    <Authenticators
                        {...{
                            authenticatorStatus,
                            canSkip: false,
                            data: authenticatorData,
                            error,
                            initAuthenticators,
                            isProcessing,
                            mfaRequired,
                            onFinish,
                            onNext: handleMFA
                        }}
                    />
                )
            case 4:
                return (
                    <DeviceType
                        {...{
                            appName: factor.appName,
                            onBack: onBackFallbackEnforceMFA,
                            onNext: onSetDeviceType,
                            provider: factor.provider,
                            title: factor.title,
                            verbiage: factor.deviceTypeVerb,
                        }}
                    />
                )
            case 5:
                return (
                    <QrCode
                        {...{
                            data: dataQRCode,
                            error,
                            factorType: factor.factorType,
                            initEnrollAuthFactor,
                            isBackAllowed: true,
                            onBack: onBackFallbackEnforceMFA,
                            onNext: handleFallbackQRCodeNext,
                            onQRCodeCannotScan,
                            provider: factor.provider,
                            qrCodeStatus,
                            title: factor.title,
                            verbiage: factor.qrCodeVerb,
                        }}
                    />
                )
            case 6:
                return (
                    <QrCodeCannotScan
                        {...{
                            data: dataQRCode,
                            isBackAllowed: true,
                            onBack: onBackFallbackEnforceMFA,
                            onNext: onQRCodeCannotScanNext,
                            provider: factor.provider,
                            title: factor.title,
                            verbiage: factor.qrCodeCannotScanVerb,
                        }}
                    />
                )
            case 7:
                return (
                    <AuthenticatorCode
                        {...{
                            authVerified,
                            error,
                            isBackAllowed: true,
                            isProcessing,
                            onBack: handleAuthenticatorCodeBack,
                            onNext: handleAuthenticatorCode,
                            resetAuthVerified,
                            title: factor.title,
                            verbiage: factor.appName,
                        }}
                    />
                )
            default:
                return <></>
        }
    }

    return (
        <>
            {appGAURL && isSafeRedirectUrl(appGAURL, IFRAME_ALLOWED_DOMAINS) ? (
                <div>
                    <iframe src={appGAURL} height={0} width={0} />
                </div>
            ) : (
                <></>
            )}
            <Grid container className={classes.gridContainerCentre}>
                {errorSession ? (
                    <ErrorPage errorTitle={translate('authenticationError')} />
                ) : mfaEnroll ? (
                    <ErrorPage errorTitle={translate('authenticationError')} errorMessage='MFA_ENROLL' />
                ) : (
                    <Grid className={classes.registrationRoot} md={5} sm={8} xs={12}>
                        {fallbackEnforceMFA ? fallbackEnforceMFAFlow(stepNumber)
                            : isMfaRequiredFlow
                                ? mandatoryMfaFlow(stepNumber)
                                : optionalMfaFlow(stepNumber)
                        }
                    </Grid>
                )}
            </Grid>
            <Grid container className={classes.gridContainerFooter}>
                <FooterNew />
            </Grid>
        </>
    )
}

Component.displayName = 'MFA'

Component.defaultProps = {
    stepNumber: 0,
}

Component.propTypes = {
    authData: PropTypes.object.isRequired,
    authenticatorData: PropTypes.string,
    authenticatorStatus: PropTypes.string,
    classes: PropTypes.shape({
        loader: PropTypes.object,
    }),
    error: PropTypes.object,
    errorSession: PropTypes.string,
    initAuthenticators: PropTypes.func,
    initEnrollAuthFactor: PropTypes.func,
    initPush: PropTypes.func,
    onActivateAuthenticatorCode: PropTypes.func,
    onBackFallbackEnforceMFA: PropTypes.func,
    onBackFallbackEnforceMFATwoSteps: PropTypes.func,
    onBackToAuthenticators: PropTypes.func,
    onBackToOptionalAuthenticators: PropTypes.func,
    onEnroll: PropTypes.func,
    onFinish: PropTypes.func,
    onQRCodeCannotScan: PropTypes.func,
    onQRCodeCannotScanNext: PropTypes.func,
    onQRCodeNext: PropTypes.func,
    onResendAuthChallenge: PropTypes.func,
    onResendChallenge: PropTypes.func,
    onSetDeviceType: PropTypes.func,
    onSetOptionalAuthenticator: PropTypes.func,
    onSkipEnrollment: PropTypes.func,
    onVerifyAccountRecovery: PropTypes.func,
    onVerifyAuthenticatorCode: PropTypes.func,
    resendSuccess: PropTypes.bool,
    resendSuccessMsg: PropTypes.string,
    stepNumber: PropTypes.string,
}

export default withStyles(Component, styles)
